import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { useSite } from '@/contexts/SiteContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuPortal,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  PlusCircle, 
  MoreVertical, 
  FileText, 
  Trash2, 
  Eye,
  Send,
  Clock,
  Edit,
  Save,
  X,
  Loader2,
  Globe,
  RefreshCw,
  AlertTriangle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  draft: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20',
  published: 'bg-green-500/10 text-green-500 border-green-500/20',
  scheduled: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
  draft_blocked: 'bg-orange-500/15 text-orange-400 border-orange-500/40',
};

const statusLabels = {
  draft: 'Ciornă',
  published: 'Publicat',
  scheduled: 'Programat',
  draft_blocked: 'Blocat',
};

// Motivul blocării (blocking_issues) — afișat doar dacă API-ul îl trimite în listă
const blockedReason = (article) =>
  article?.status !== 'draft_blocked'
    ? undefined
    : (article.blocking_issues || []).length
      ? `Blocat la publicarea automată:\n• ${article.blocking_issues.join('\n• ')}`
      : 'Blocat la publicarea automată (motivul nu e disponibil în listă)';

export default function ArticlesPage() {
  const [articles, setArticles] = useState([]);
  const [wpSites, setWpSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({ title: '', content: '', keywords: '' });
  const [saving, setSaving] = useState(false);
  const [publishingSiteId, setPublishingSiteId] = useState(null);
  const [selectedSiteForPublish, setSelectedSiteForPublish] = useState('');
  const [showPublishDialog, setShowPublishDialog] = useState(false);
  const [articleToPublish, setArticleToPublish] = useState(null);
  // publicarea unui articol draft_blocked: doar după confirmare, cu planul (actualizare/postare nouă) dat de backend
  const [blockedToPublish, setBlockedToPublish] = useState(null);
  const [blockedPlan, setBlockedPlan] = useState(null);
  const { currentSiteId, sites, loading: sitesLoading } = useSite();
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  // selecția pentru care e valabilă lista: id-ul site-ului sau 'all' („Toate site-urile”, ales explicit)
  const selectionKey = currentSiteId || 'all';
  const latestKeyRef = useRef(selectionKey);
  const abortRef = useRef(null);

  // site-urile WordPress vin din SiteContext (aceeași listă), fără o cerere în plus
  useEffect(() => {
    setWpSites(sites || []);
  }, [sites]);

  // O singură cerere, doar pentru selecția curentă; nu se pornește până nu sunt încărcate site-urile
  // (altfel cererea fără site_id pornea înaintea celei cu site_id și o putea suprascrie)
  useEffect(() => {
    latestKeyRef.current = selectionKey;
    if (sitesLoading) return undefined;
    setArticles([]);
    setLoading(true);
    fetchArticles(selectionKey);
    return () => abortRef.current?.abort();
  }, [selectionKey, sitesLoading]);

  const fetchArticles = async (key) => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const params = key !== 'all' ? { site_id: key } : {};
      const res = await axios.get(`${API}/articles`, { headers: getAuthHeaders(), params, signal: controller.signal });
      // răspunsul unei cereri vechi (alt site / altă selecție) nu suprascrie lista curentă
      if (key !== latestKeyRef.current) return;
      setArticles(key !== 'all' ? (res.data || []).filter((a) => a.site_id === key) : (res.data || []));
    } catch (error) {
      if (axios.isCancel(error) || error?.name === 'CanceledError') return;
      if (key === latestKeyRef.current) toast.error('Eroare la încărcarea datelor');
    } finally {
      if (key === latestKeyRef.current) setLoading(false);
    }
  };

  const fetchData = () => fetchArticles(latestKeyRef.current);

  const handleDelete = async (id) => {
    if (!window.confirm('Ești sigur că vrei să ștergi acest articol?')) return;
    try {
      await axios.delete(`${API}/articles/${id}`, { headers: getAuthHeaders() });
      toast.success('Articol șters');
      fetchData();
    } catch (error) {
      toast.error('Eroare la ștergere');
    }
  };

  const handlePublishClick = (article) => {
    if (wpSites.length === 0) {
      toast.error('Niciun site WordPress conectat. Du-te la WordPress pentru a adăuga un site.');
      return;
    }
    if (article.status === 'draft_blocked') {
      openBlockedConfirm(article);
      return;
    }
    if (wpSites.length === 1) {
      handlePublish(article.id, wpSites[0].id);
    } else {
      setArticleToPublish(article);
      setSelectedSiteForPublish(wpSites[0].id);
      setShowPublishDialog(true);
    }
  };

  const handlePublish = async (articleId, siteId) => {
    setPublishingSiteId(siteId);
    try {
      const res = await axios.post(`${API}/wordpress/publish/${articleId}?site_id=${siteId}`, {}, { headers: getAuthHeaders() });
      const site = wpSites.find(s => s.id === siteId);
      toast.success(res.data?.mode === 'update'
        ? `Postarea existentă #${res.data.wp_post_id} a fost actualizată și publicată pe ${site?.site_name || site?.site_url}!`
        : `Articol publicat pe ${site?.site_name || site?.site_url}!`);
      setShowPublishDialog(false);
      setArticleToPublish(null);
      setBlockedToPublish(null);
      setBlockedPlan(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la publicare');
    } finally {
      setPublishingSiteId(null);
    }
  };

  // planul de publicare cerut backend-ului cu dry_run=true (nicio scriere): actualizare #id sau postare nouă
  const openBlockedConfirm = async (article) => {
    setBlockedToPublish(article);
    setBlockedPlan({ loading: true });
    try {
      const siteId = article.site_id || wpSites[0]?.id;
      const res = await axios.post(`${API}/wordpress/publish/${article.id}`, {}, {
        headers: getAuthHeaders(), params: { site_id: siteId, dry_run: true },
      });
      setBlockedPlan({ loading: false, ...res.data });
    } catch (error) {
      setBlockedPlan({ loading: false, error: error.response?.data?.detail || 'Verificarea postării în WordPress a eșuat' });
    }
  };

  const [regenerating, setRegenerating] = useState(null);

  const handleRegenerate = async (articleId) => {
    if (!window.confirm('Regenerezi articolul în format HTML? Conținutul vechi va fi înlocuit.')) return;
    setRegenerating(articleId);
    try {
      const response = await axios.post(`${API}/articles/${articleId}/regenerate`, {}, { headers: getAuthHeaders() });
      toast.success('Articol regenerat în HTML!');
      fetchData();
      if (selectedArticle?.id === articleId) {
        setSelectedArticle(response.data);
        setEditData({
          title: response.data.title,
          content: response.data.content,
          keywords: response.data.keywords.join(', ')
        });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la regenerare');
    } finally {
      setRegenerating(null);
    }
  };

  const handleViewArticle = (article) => {
    setSelectedArticle(article);
    setEditMode(false);
    setEditData({
      title: article.title,
      content: article.content,
      keywords: article.keywords.join(', ')
    });
  };

  const handleEditToggle = () => {
    if (!editMode) {
      setEditData({
        title: selectedArticle.title,
        content: selectedArticle.content,
        keywords: selectedArticle.keywords.join(', ')
      });
    }
    setEditMode(!editMode);
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      const keywords = editData.keywords.split(',').map(k => k.trim()).filter(k => k);
      await axios.put(`${API}/articles/${selectedArticle.id}`, {
        title: editData.title,
        content: editData.content,
        keywords
      }, { headers: getAuthHeaders() });
      
      toast.success('Articol actualizat cu succes!');
      setEditMode(false);
      fetchData();
      
      setSelectedArticle({
        ...selectedArticle,
        title: editData.title,
        content: editData.content,
        keywords,
        word_count: editData.content.split(/\s+/).length
      });
    } catch (error) {
      toast.error('Eroare la salvare');
    } finally {
      setSaving(false);
    }
  };

  const handleCloseModal = () => {
    setSelectedArticle(null);
    setEditMode(false);
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 bg-secondary rounded w-48" />
        <div className="grid gap-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-32 bg-secondary rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 w-full max-w-full min-w-0" data-testid="articles-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold">Articole</h1>
          <p className="text-muted-foreground mt-1">
            Gestionează și generează articole SEO
          </p>
        </div>
        <Button 
          className="bg-primary text-primary-foreground hover:bg-primary/90 neon-glow-hover"
          onClick={() => navigate('/articles/new')}
          data-testid="generate-article-button"
        >
          <PlusCircle className="w-4 h-4 mr-2" />
          Generează Articol
        </Button>
      </div>

      {/* Articles List */}
      {articles.length === 0 ? (
        <Card className="bg-card border-border">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <FileText className="w-16 h-16 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">Niciun articol încă</h3>
            <p className="text-muted-foreground text-center mb-6">
              Începe prin a genera primul tău articol SEO optimizat
            </p>
            <Button 
              className="bg-primary text-primary-foreground"
              onClick={() => navigate('/articles/new')}
            >
              <PlusCircle className="w-4 h-4 mr-2" />
              Primul Articol
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 w-full max-w-full min-w-0">
          {articles.map((article) => (
            <Card 
              key={article.id} 
              data-site-id={article.site_id}
              data-status={article.status}
              className="bg-card border-border hover:border-primary/30 transition-colors w-full max-w-full min-w-0 overflow-hidden"
            >
              <CardContent className="pt-6 min-w-0">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4 min-w-0">
                  <div className="flex-1 min-w-0 w-full">
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-2">
                      <Badge
                        className={`${statusColors[article.status] || statusColors.draft}${article.status === 'draft_blocked' ? ' cursor-help' : ''}`}
                        title={blockedReason(article)}
                        data-testid={`article-status-${article.id}`}
                      >
                        {statusLabels[article.status] || article.status}
                      </Badge>
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {new Date(article.created_at).toLocaleDateString('ro-RO')}
                      </span>
                    </div>
                    <h3 className="font-heading font-semibold text-lg mb-2 break-words [overflow-wrap:anywhere] line-clamp-2" title={article.title}>
                      {article.title}
                    </h3>
                    <div className="flex flex-wrap gap-2 mb-3">
                      {article.keywords.slice(0, 3).map((kw, i) => (
                        <span 
                          key={i}
                          className="text-xs px-2 py-1 rounded-full bg-secondary text-muted-foreground max-w-full break-words [overflow-wrap:anywhere]"
                        >
                          {kw}
                        </span>
                      ))}
                      {article.keywords.length > 3 && (
                        <span className="text-xs px-2 py-1 rounded-full bg-secondary text-muted-foreground">
                          +{article.keywords.length - 3}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
                      <span>{article.word_count} cuvinte</span>
                      <span>Scor SEO: {article.seo_score}%</span>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center justify-end gap-2 shrink-0 self-end sm:self-start">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="shrink-0" data-testid={`article-menu-${article.id}`}>
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-card border-border">
                      <DropdownMenuItem 
                        onClick={() => handleViewArticle(article)}
                        className="cursor-pointer"
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        Vizualizează / Editează
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={() => handleRegenerate(article.id)}
                        className="cursor-pointer"
                        disabled={regenerating === article.id}
                      >
                        {regenerating === article.id ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <RefreshCw className="w-4 h-4 mr-2" />
                        )}
                        Regenerează (HTML)
                      </DropdownMenuItem>
                      {(article.status === 'draft' || article.status === 'draft_blocked') && wpSites.length > 0 && (
                        <DropdownMenuItem 
                          onClick={() => handlePublishClick(article)}
                          className="cursor-pointer"
                        >
                          <Send className="w-4 h-4 mr-2" />
                          Publică pe WordPress
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem 
                        onClick={() => handleDelete(article.id)}
                        className="cursor-pointer text-destructive"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Șterge
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Publish Site Selection Dialog */}
      <Dialog open={showPublishDialog} onOpenChange={setShowPublishDialog}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="font-heading">Alege site-ul pentru publicare</DialogTitle>
            <DialogDescription>
              Selectează pe care site WordPress vrei să publici articolul
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Site WordPress</Label>
              <Select value={selectedSiteForPublish} onValueChange={setSelectedSiteForPublish}>
                <SelectTrigger className="bg-secondary border-border">
                  <SelectValue placeholder="Selectează site" />
                </SelectTrigger>
                <SelectContent className="bg-card border-border">
                  {wpSites.map(site => (
                    <SelectItem key={site.id} value={site.id}>
                      <div className="flex items-center gap-2">
                        <Globe className="w-4 h-4" />
                        {site.site_name || site.site_url}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowPublishDialog(false)}>
                Anulează
              </Button>
              <Button 
                className="bg-primary text-primary-foreground"
                onClick={() => handlePublish(articleToPublish?.id, selectedSiteForPublish)}
                disabled={publishingSiteId !== null}
              >
                {publishingSiteId ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Se publică...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Publică
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Article Preview/Edit Modal */}
      <Dialog open={!!selectedArticle} onOpenChange={(open) => !open && handleCloseModal()}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden bg-card border-border">
          <DialogHeader>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 min-w-0">
              <div className="flex-1 min-w-0">
                {editMode ? (
                  <Input
                    value={editData.title}
                    onChange={(e) => setEditData({...editData, title: e.target.value})}
                    className="text-xl font-heading font-semibold bg-secondary border-border"
                    data-testid="edit-article-title"
                  />
                ) : (
                  <DialogTitle className="font-heading text-xl break-words [overflow-wrap:anywhere] pr-6">{selectedArticle?.title}</DialogTitle>
                )}
              </div>
              <div className="flex flex-wrap gap-2 shrink-0 sm:ml-4">
                {editMode ? (
                  <>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={handleEditToggle}
                      disabled={saving}
                    >
                      <X className="w-4 h-4 mr-1" />
                      Anulează
                    </Button>
                    <Button
                      size="sm"
                      className="bg-primary text-primary-foreground"
                      onClick={handleSaveEdit}
                      disabled={saving}
                      data-testid="save-article-button"
                    >
                      {saving ? (
                        <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                      ) : (
                        <Save className="w-4 h-4 mr-1" />
                      )}
                      Salvează
                    </Button>
                  </>
                ) : (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleEditToggle}
                    className="border-border"
                    data-testid="edit-article-button"
                  >
                    <Edit className="w-4 h-4 mr-1" />
                    Editează
                  </Button>
                )}
              </div>
            </div>
            {selectedArticle && (
              <DialogDescription className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2">
                <Badge className={statusColors[selectedArticle.status] || statusColors.draft} title={blockedReason(selectedArticle)}>
                  {statusLabels[selectedArticle.status] || selectedArticle.status}
                </Badge>
                <span>{selectedArticle.word_count} cuvinte</span>
                <span>Scor SEO: {selectedArticle.seo_score}%</span>
              </DialogDescription>
            )}
          </DialogHeader>
          
          <div className="overflow-y-auto overflow-x-hidden max-h-[60vh] py-4 min-w-0">
            {editMode ? (
              <div className="space-y-4">
                <div>
                  <Label className="text-sm text-muted-foreground">Cuvinte-cheie (separate prin virgulă)</Label>
                  <Input
                    value={editData.keywords}
                    onChange={(e) => setEditData({...editData, keywords: e.target.value})}
                    className="mt-1 bg-secondary border-border"
                    placeholder="SEO, marketing, content"
                    data-testid="edit-article-keywords"
                  />
                </div>
                <div>
                  <Label className="text-sm text-muted-foreground">Conținut articol</Label>
                  <Textarea
                    value={editData.content}
                    onChange={(e) => setEditData({...editData, content: e.target.value})}
                    className="mt-1 bg-secondary border-border min-h-[400px] font-mono text-sm"
                    data-testid="edit-article-content"
                  />
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {selectedArticle?.status === 'draft_blocked' && (
                  <div className="rounded-md border border-orange-500/40 bg-orange-500/10 p-3 text-sm text-orange-300 break-words [overflow-wrap:anywhere]" data-testid="blocked-reasons">
                    <p className="font-semibold text-orange-400 mb-1">Articol blocat la publicarea automată</p>
                    {(selectedArticle.blocking_issues || []).length ? (
                      <ul className="list-disc pl-5 space-y-1">
                        {selectedArticle.blocking_issues.map((issue, i) => <li key={i}>{issue}</li>)}
                      </ul>
                    ) : (
                      <p>Motivul nu e disponibil pentru acest articol.</p>
                    )}
                  </div>
                )}
                <div className="flex flex-wrap gap-2">
                  {selectedArticle?.keywords.map((kw, i) => (
                    <span 
                      key={i}
                      className="text-xs px-3 py-1 rounded-full bg-primary/10 text-primary border border-primary/20 max-w-full break-words [overflow-wrap:anywhere]"
                    >
                      {kw}
                    </span>
                  ))}
                </div>
                <div className="prose prose-invert max-w-none min-w-0 break-words [overflow-wrap:anywhere]">
                  <style>{`
                    .article-content h2 { font-size: 1.5rem; font-weight: 700; margin: 1.5rem 0 0.75rem; color: var(--primary); }
                    .article-content h3 { font-size: 1.25rem; font-weight: 600; margin: 1.25rem 0 0.5rem; }
                    .article-content p { margin: 0.75rem 0; line-height: 1.8; }
                    .article-content ul, .article-content ol { margin: 0.75rem 0; padding-left: 1.5rem; }
                    .article-content li { margin: 0.25rem 0; }
                    .article-content strong { font-weight: 700; }
                    .article-content a { color: var(--primary); text-decoration: underline; word-break: break-all; }
                    .article-content img { max-width: 100%; height: auto; }
                    .article-content table { display: block; max-width: 100%; overflow-x: auto; }
                  `}</style>
                  <div 
                    className="text-foreground leading-relaxed article-content"
                    dangerouslySetInnerHTML={{ __html: selectedArticle?.content || '' }}
                  />
                </div>
              </div>
            )}
          </div>

          {!editMode && (selectedArticle?.status === 'draft' || selectedArticle?.status === 'draft_blocked') && wpSites.length > 0 && (
            <div className="flex flex-wrap justify-end gap-2 pt-4 border-t border-border">
              <Button
                className="bg-primary text-primary-foreground hover:bg-primary/90"
                onClick={() => handlePublishClick(selectedArticle)}
              >
                <Send className="w-4 h-4 mr-2" />
                Publică pe WordPress
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Confirmare publicare articol blocat */}
      <Dialog open={!!blockedToPublish} onOpenChange={(open) => { if (!open) { setBlockedToPublish(null); setBlockedPlan(null); } }}>
        <DialogContent className="bg-card border-border max-w-2xl" data-testid="blocked-publish-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2 break-words [overflow-wrap:anywhere] pr-6">
              <AlertTriangle className="w-5 h-5 text-orange-400 shrink-0" />
              Publici un articol blocat?
            </DialogTitle>
            <DialogDescription className="break-words [overflow-wrap:anywhere]">{blockedToPublish?.title}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2 min-w-0">
            <div className="rounded-md border border-orange-500/40 bg-orange-500/10 p-3 text-sm text-orange-300 break-words [overflow-wrap:anywhere]">
              <p className="font-semibold text-orange-400 mb-1">Motivele blocării</p>
              {(blockedToPublish?.blocking_issues || []).length ? (
                <ul className="list-disc pl-5 space-y-1" data-testid="blocked-publish-issues">
                  {blockedToPublish.blocking_issues.map((issue, i) => <li key={i}>{issue}</li>)}
                </ul>
              ) : (
                <p>Motivul nu e disponibil pentru acest articol.</p>
              )}
            </div>
            <div className="rounded-md border border-border bg-secondary/50 p-3 text-sm" data-testid="blocked-publish-plan">
              {blockedPlan?.loading && (
                <span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Se verifică postarea în WordPress…</span>
              )}
              {blockedPlan?.error && <span className="text-destructive">{blockedPlan.error}</span>}
              {blockedPlan?.mode === 'update' && (
                <span>Se actualizează postarea existentă #{blockedPlan.wp_post_id} (conținutul curent, status publicat), fără postare nouă.</span>
              )}
              {blockedPlan?.mode === 'new' && (
                <span>Se creează postare nouă în WordPress ({blockedPlan.reason}).</span>
              )}
              {blockedPlan && !blockedPlan.loading && !blockedPlan.error && (
                <p className="text-muted-foreground mt-2">Apoi: indexare, linkuri interne și canalele sociale configurate (cele deja postate nu se repostează).</p>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-2 justify-end">
            <Button variant="outline" onClick={() => { setBlockedToPublish(null); setBlockedPlan(null); }}>Anulează</Button>
            <Button
              className="bg-orange-500 text-white hover:bg-orange-500/90"
              disabled={!blockedPlan || blockedPlan.loading || !!blockedPlan.error || !!publishingSiteId}
              onClick={() => handlePublish(blockedToPublish.id, blockedToPublish.site_id || wpSites[0]?.id)}
              data-testid="blocked-publish-confirm"
            >
              {publishingSiteId ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Publică oricum
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
